/**
 * FLETE (freight) utilities for handling shipping charges
 * based on delivery region
 */

// Feature flag to enable/disable automatic FLETE addition
// Set to true to re-enable the flete auto-add feature
export const FLETE_AUTO_ADD_ENABLED = false;

// FLETE product constants
export const FLETE_PRODUCT_ID = "e42765c3-6ad4-457b-a48b-24ba4b2af9cc";
export const FLETE_PRODUCT_SHORT_NAME = "FLETE";
export const FLETE_PRICE = 30000;

/**
 * Regions that do NOT require FLETE (freight) charges or special pricing review.
 * These are the Metropolitana, Fifth (Valparaíso), and Sixth (O'Higgins) regions.
 *
 * Based on actual database values:
 * - Metropolitana: "metropolitana", "Metropolitana", "METROPOLITANA DE SANTIAGO",
 *   "Región Metropolitana (de Santiago)", "Región Metropolitana de Santiago", "XIII REGION METROPOLITANA"
 * - Fifth (Valparaíso): "Quinta Región (de Valparaíso)", "Región de Valparaíso", "Valparaíso"
 * - Sixth (O'Higgins): "Sexta Región (del Libertador B.O'higgins", "O'Higgins"
 */
export const FLETE_EXEMPT_REGIONS_PATTERNS: RegExp[] = [
  // Metropolitana variations
  /metropolitana/i,
  /xiii.*region/i,
  /region.*metropolitana/i,
  /santiago/i,

  // Fifth Region (Valparaíso) variations
  /valparaíso/i,
  /valparaiso/i,
  /quinta.*región/i,
  /quinta.*region/i,
  /región.*valparaíso/i,
  /region.*valparaiso/i,

  // Sixth Region (O'Higgins) variations
  /o'higgins/i,
  /o'higgins/i,
  /ohiggins/i,
  /sexta.*región/i,
  /sexta.*region/i,
  /libertador/i,
];

/**
 * Checks if a delivery state/region requires FLETE charges
 * @param deliveryState The delivery state/region name
 * @returns true if FLETE is required, false if exempt
 */
export const requiresFlete = (
  deliveryState: string | null | undefined
): boolean => {
  // Feature flag check - return false if auto-add is disabled
  if (!FLETE_AUTO_ADD_ENABLED) {
    return false;
  }

  if (!deliveryState) {
    return false; // No state means cannot determine, default to no FLETE
  }

  // Check if the state matches any exempt pattern
  const isExempt = FLETE_EXEMPT_REGIONS_PATTERNS.some((pattern) =>
    pattern.test(deliveryState)
  );

  return !isExempt;
};

/**
 * Checks if an order item is the FLETE product
 * @param productId The product ID to check
 * @returns true if the product is FLETE
 */
export const isFleteProduct = (productId: string): boolean => {
  return productId === FLETE_PRODUCT_ID;
};

/**
 * Checks if a delivery region is remote (outside RM, Valparaíso, O'Higgins)
 * This is used to determine if a sales request requires pricing review.
 * @param deliveryState The delivery state/region name
 * @returns true if the region is remote (not RM/Valparaíso/O'Higgins), false otherwise
 */
export const isRemoteRegion = (
  deliveryState: string | null | undefined
): boolean => {
  if (!deliveryState) {
    return false; // Cannot determine, default to not remote
  }

  // Check if the state matches any exempt pattern (RM, Valparaíso, O'Higgins)
  const isExempt = FLETE_EXEMPT_REGIONS_PATTERNS.some((pattern) =>
    pattern.test(deliveryState)
  );

  return !isExempt; // Remote if NOT in exempt regions
};
